'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { styled, alpha } from '@mui/material/styles';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { useEventCalendarView } from '@mui/x-scheduler-headless/use-event-calendar-view';
import { sortEventOccurrences } from '@mui/x-scheduler-headless/sort-event-occurrences';
import { useExtractEventCalendarParameters } from '@mui/x-scheduler-headless/use-event-calendar';
import { eventCalendarAgendaSelectors } from '@mui/x-scheduler-headless/event-calendar-selectors';
import { useEventOccurrencesGroupedByDay } from '@mui/x-scheduler-headless/use-event-occurrences-grouped-by-day';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-headless/use-event-calendar-store-context';
import { AGENDA_VIEW_DAYS_AMOUNT } from '@mui/x-scheduler-headless/constants';
import { schedulerNowSelectors, schedulerOtherSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import clsx from 'clsx';
import { EventCalendarProvider } from "../internals/components/EventCalendarProvider.mjs";
import { EventItem } from "../internals/components/event/event-item/EventItem.mjs";
import { EventSkeleton } from "../internals/components/event-skeleton/index.mjs";
import { useEventCalendarStyledContext } from "../event-calendar/EventCalendarStyledContext.mjs";
import { EventDialogProvider, EventDialogTrigger } from "../internals/components/event-dialog/index.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const AgendaViewRoot = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'AgendaView'
})(({
  theme
}) => ({
  width: '100%',
  maxHeight: '100%',
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflowY: 'auto',
  position: 'relative'
}));
const AgendaViewRow = styled('section', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewRow'
})(({
  theme
}) => ({
  display: 'grid',
  gridTemplateColumns: '190px 1fr',
  '&:not(:last-child)': {
    borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const DayHeaderCell = styled('header', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewDayHeaderCell'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  borderRight: `1px solid ${(theme.vars || theme).palette.divider}`,
  padding: theme.spacing(2),
  gap: theme.spacing(0.5),
  '&[data-current]': {
    backgroundColor: theme.vars ? `rgba(${theme.vars.palette.primary.lightChannel} / 0.05)` : alpha(theme.palette.primary.light, 0.05),
    color: (theme.vars || theme).palette.primary.main
  }
}));
const DayNumberCell = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewDayNumberCell'
})(({
  theme
}) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1,
  minWidth: '4ch',
  textAlign: 'center',
  color: (theme.vars || theme).palette.text.primary,
  '&[data-current]': {
    color: (theme.vars || theme).palette.primary.main
  }
}));
const WeekDayCell = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewWeekDayCell'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));
const AgendaWeekDayNameLabel = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewWeekDayNameLabel'
})(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1,
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
}));
const AgendaYearAndMonthLabel = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewYearAndMonthLabel'
})(({
  theme
}) => ({
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1,
  color: (theme.vars || theme).palette.text.secondary,
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '[data-current] &': {
    color: 'inherit'
  }
}));
const EventsList = styled('ul', {
  name: 'MuiEventCalendar',
  slot: 'AgendaViewEventsList'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  margin: 0,
  listStyle: 'none',
  flexGrow: 1
}));
const AGENDA_VIEW_CONFIG = {
  siblingVisibleDateGetter: ({
    state,
    delta
  }) => state.adapter.addDays(schedulerOtherSelectors.visibleDate(state), AGENDA_VIEW_DAYS_AMOUNT * delta),
  visibleDaysSelector: eventCalendarAgendaSelectors.visibleDays
};

/**
 * An Agenda View to use inside the Event Calendar.
 */
export const AgendaView = /*#__PURE__*/React.memo(/*#__PURE__*/React.forwardRef(function AgendaView(props, forwardedRef) {
  // Context hooks
  const adapter = useAdapterContext();
  const {
    classes
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();

  // Ref hooks
  const containerRef = React.useRef(null);
  const handleRef = useMergedRefs(forwardedRef, containerRef);

  // Selector hooks
  const now = useStore(store, schedulerNowSelectors.nowUpdatedEveryMinute);

  // Feature hooks
  const {
    days
  } = useEventCalendarView(AGENDA_VIEW_CONFIG);
  const occurrencesMap = useEventOccurrencesGroupedByDay({
    days
  });

  // Selector hooks
  const isLoading = useStore(store, schedulerOtherSelectors.isLoading);
  const daysWithOccurrences = React.useMemo(() => days.map(date => {
    const occurrences = sortEventOccurrences(occurrencesMap.get(date.key) || []);
    return {
      date,
      occurrences
    };
  }), [days, occurrencesMap]);
  return /*#__PURE__*/_jsx(AgendaViewRoot, _extends({}, props, {
    ref: handleRef,
    className: clsx(props.className, classes.agendaView),
    children: daysWithOccurrences.map(({
      date,
      occurrences
    }) => /*#__PURE__*/_jsxs(AgendaViewRow, {
      className: classes.agendaViewRow,
      id: `AgendaViewRow-${date.key}`,
      "aria-labelledby": `DayHeaderCell-${date.key}`,
      children: [/*#__PURE__*/_jsxs(DayHeaderCell, {
        className: classes.agendaViewDayHeaderCell,
        id: `DayHeaderCell-${date.key}`,
        "aria-label": `${adapter.format(date.value, 'weekday')} ${adapter.format(date.value, 'dayOfMonth')}`,
        "data-current": adapter.isSameDay(date.value, now) ? '' : undefined,
        children: [/*#__PURE__*/_jsx(DayNumberCell, {
          className: classes.agendaViewDayNumberCell,
          "data-current": adapter.isSameDay(date.value, now) ? '' : undefined,
          children: adapter.format(date.value, 'dayOfMonth')
        }), /*#__PURE__*/_jsxs(WeekDayCell, {
          className: classes.agendaViewWeekDayCell,
          children: [/*#__PURE__*/_jsx(AgendaWeekDayNameLabel, {
            className: classes.agendaViewWeekDayNameLabel,
            style: {
              '--number-of-lines': 1
            },
            children: adapter.format(date.value, 'weekday')
          }), /*#__PURE__*/_jsxs(AgendaYearAndMonthLabel, {
            className: classes.agendaViewYearAndMonthLabel,
            style: {
              '--number-of-lines': 1
            },
            children: [adapter.format(date.value, 'monthFullLetter'), ",", ' ', adapter.format(date.value, 'yearPadded')]
          })]
        })]
      }), /*#__PURE__*/_jsxs(EventsList, {
        className: classes.agendaViewEventsList,
        children: [isLoading && /*#__PURE__*/_jsx("li", {
          className: classes.agendaViewEventListItem,
          children: /*#__PURE__*/_jsx(EventSkeleton, {
            "data-variant": "agenda"
          })
        }), !isLoading && occurrences.map(occurrence => /*#__PURE__*/_jsx("li", {
          className: classes.agendaViewEventListItem,
          children: /*#__PURE__*/_jsx(EventDialogTrigger, {
            occurrence: occurrence,
            children: /*#__PURE__*/_jsx(EventItem, {
              occurrence: occurrence,
              date: date,
              variant: "regular",
              ariaLabelledBy: `DayHeaderCell-${date.key}`
            })
          })
        }, occurrence.key))]
      })]
    }, date.key))
  }));
}));

/**
 * An Agenda View that can be used outside of the Event Calendar.
 */
if (process.env.NODE_ENV !== "production") AgendaView.displayName = "AgendaView";
export const StandaloneAgendaView = /*#__PURE__*/React.forwardRef(function StandaloneAgendaView(props, forwardedRef) {
  const {
    parameters,
    forwardedProps
  } = useExtractEventCalendarParameters(props);
  return /*#__PURE__*/_jsx(EventCalendarProvider, _extends({}, parameters, {
    children: /*#__PURE__*/_jsx(EventDialogProvider, {
      children: /*#__PURE__*/_jsx(AgendaView, _extends({
        ref: forwardedRef
      }, forwardedProps))
    })
  }));
});
if (process.env.NODE_ENV !== "production") StandaloneAgendaView.displayName = "StandaloneAgendaView";